import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import Spacer from "components/Spacer";
import { getSanityImage, shopV3Dataset } from "lib/sanity";
import React from "react";
import { Pressable, TextStyle, View } from "react-native";
import { formatCurrency } from "utils/appUtils";
import {
  defaultVariant,
  fullPriceOf,
  heroGradient,
  LINE,
  LiveProduct,
  LiveVariant,
  monthlyPriceOf,
  PANEL,
  PAPER,
  PriceDisplayStrategy,
  unitPriceLabel,
} from "../helpers";

const HERO_PRICE: TextStyle = {
  color: "#ffffff",
  fontSize: 20,
  fontWeight: "600",
};
const HERO_SUB: TextStyle = { color: "rgba(255,255,255,0.85)", fontSize: 12 };

interface Props {
  product: LiveProduct;
  priceStrategy: PriceDisplayStrategy;
  primaryColor: string;
  selectedVariantKey?: string;
  quantity: number;
  onSelectVariant: (variantKey: string) => void;
  onQuantityChange: (quantity: number) => void;
  onPurchase: (selected: LiveVariant, quantity: number) => void;
  onOpenComparison: (product: LiveProduct) => void;
  onGift: (planPackageId: number) => void;
}

// Payment story for the hero's bottom-right corner: monthly + total +
// number of payments when the plan bills monthly; total or "Free" otherwise.
const HeroPrice = ({ live }: { live: LiveVariant }) => {
  const { planPackage } = live;
  const full = fullPriceOf(planPackage);
  if (full === 0) return <Text style={HERO_PRICE}>Free</Text>;
  if (!planPackage.billingIntervals.includes("MONTHLY")) {
    return <Text style={HERO_PRICE}>{formatCurrency(full)}</Text>;
  }
  return (
    <>
      <Text style={HERO_PRICE}>
        {formatCurrency(monthlyPriceOf(planPackage))}
        <Text style={{ ...HERO_SUB, fontSize: 13 }}>/mo</Text>
      </Text>
      <Text style={{ ...HERO_SUB, marginTop: 2 }}>
        {formatCurrency(full)} total · {planPackage.numBillingMonths || 12}{" "}
        payments
      </Text>
    </>
  );
};

// One merchandised product: gradient hero (name/tagline/payment story),
// then variant selector, quantity, seat map, facts, rules, and CTAs.
const ProductCardV3 = ({
  product,
  priceStrategy,
  primaryColor,
  selectedVariantKey,
  quantity,
  onSelectVariant,
  onQuantityChange,
  onPurchase,
  onOpenComparison,
  onGift,
}: Props) => {
  const { product: doc } = product;
  const selected =
    product.variants.find(
      ({ variant }) => variant.key === selectedVariantKey,
    ) ?? defaultVariant(product.variants);
  const benefits =
    (selected.variant.benefits?.length
      ? selected.variant.benefits
      : doc.benefits) ?? [];
  const maxPerOrder = doc.maxPerOrder ?? 1;

  const adjustQuantity = (delta: number) =>
    onQuantityChange(Math.min(maxPerOrder, Math.max(1, quantity + delta)));

  const stepperButton = (
    id: "minus" | "plus",
    label: string,
    delta: number,
  ) => (
    <Pressable
      testID={`v3-quantity-${id}-${doc.slug.current}`}
      accessibilityRole="button"
      onPress={() => adjustQuantity(delta)}
      className="items-center justify-center"
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        borderWidth: 2,
        borderColor: LINE,
      }}
    >
      <Text category="h6" style={{ color: primaryColor }}>
        {label}
      </Text>
    </Pressable>
  );

  return (
    <View
      testID={`v3-product-${doc.slug.current}`}
      className="border rounded-2xl mb-4"
      style={{ borderColor: LINE, backgroundColor: PAPER, overflow: "hidden" }}
    >
      {/* Product hero (prototype's fgm-plan-hero): name + tagline on the
          tenant gradient, payment story bottom-right. */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 12,
          padding: 16,
          backgroundImage: heroGradient(primaryColor),
          backgroundColor: primaryColor,
        }}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              ...HERO_PRICE,
              lineHeight: 24,
              letterSpacing: -0.3,
            }}
          >
            {doc.name}
          </Text>
          {doc.tagline ? (
            <Text
              style={{
                color: "rgba(255,255,255,0.88)",
                fontSize: 13,
                lineHeight: 18,
                marginTop: 4,
                maxWidth: 260,
              }}
            >
              {doc.tagline}
            </Text>
          ) : null}
        </View>
        <View className="items-end" style={{ flexShrink: 0 }}>
          {doc.kind === "season" ? (
            /* Season plans are priced per selected seat, not per plan. */
            <Text style={{ ...HERO_SUB, fontSize: 14 }}>Priced by seat</Text>
          ) : (
            <HeroPrice live={selected} />
          )}
        </View>
      </div>

      <View className="p-4">
        {product.variants.length > 1 ? (
          <View className="flex-row flex-wrap gap-2 mt-2">
            {product.variants.map((live) => {
              const isSelected = live.variant.key === selected.variant.key;
              return (
                <Pressable
                  key={live.variant.key}
                  testID={`v3-variant-${live.variant.key}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isSelected }}
                  onPress={() => onSelectVariant(live.variant.key)}
                  style={{
                    borderWidth: 2,
                    borderColor: isSelected ? primaryColor : LINE,
                    borderRadius: 10,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                  }}
                >
                  <Text category="s2">
                    {live.variant.label}
                    {live.variant.popular ? " ★" : ""}
                  </Text>
                  {doc.kind !== "season" ? (
                    <Text category="c1" appearance="hint">
                      {unitPriceLabel(live.planPackage, priceStrategy)}
                    </Text>
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        ) : null}

        {maxPerOrder > 1 ? (
          /* Quantity stepper for multi-buy products (e.g. Single Pass,
             voucher packs). Cap is the CMS maxPerOrder guardrail; backend
             eligibility stays authoritative. */
          <View
            className="flex-row items-center justify-between mt-3 px-3 py-2"
            style={{ borderWidth: 1, borderColor: LINE, borderRadius: 12 }}
          >
            <View>
              <Text category="s2">Quantity</Text>
              <Text category="c1" appearance="hint">
                Up to {maxPerOrder} per order
              </Text>
            </View>
            <View className="flex-row items-center" style={{ gap: 16 }}>
              {stepperButton("minus", "−", -1)}
              <Text
                category="s1"
                testID={`v3-quantity-${doc.slug.current}`}
                style={{ minWidth: 20, textAlign: "center" }}
              >
                {quantity}
              </Text>
              {stepperButton("plus", "+", 1)}
            </View>
          </View>
        ) : null}

        {doc.seatMapImage?.asset?._ref ? (
          /* Seat map / supplemental image (prototype's fgm-map-wrap). */
          <View
            className="mt-3"
            style={{
              borderWidth: 1,
              borderColor: LINE,
              borderRadius: 12,
              overflow: "hidden",
              backgroundColor: PANEL,
            }}
          >
            {doc.mapLabel ? (
              <View className="px-3 pt-3">
                <Text category="s2">{doc.mapLabel}</Text>
              </View>
            ) : null}
            <img
              src={getSanityImage(doc.seatMapImage.asset._ref, shopV3Dataset)}
              alt={doc.mapLabel || doc.name}
              style={{ width: "100%", display: "block", marginTop: 8 }}
            />
          </View>
        ) : null}

        {doc.facts?.length ? (
          /* Facts grid (prototype's fgm-plan-facts): centered label/value
             cells separated by hairlines inside one rounded border. */
          <View
            className="flex-row mt-3"
            style={{
              borderWidth: 1,
              borderColor: LINE,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            {doc.facts.map((fact, factIndex) => (
              <View
                key={fact._key}
                className="flex-1 items-center px-1"
                style={{
                  paddingVertical: 10,
                  backgroundColor: PANEL,
                  borderLeftWidth: factIndex === 0 ? 0 : 1,
                  borderLeftColor: LINE,
                }}
              >
                <Text category="c1" appearance="hint">
                  {fact.label}
                </Text>
                <Text category="s2" style={{ marginTop: 2 }}>
                  {fact.value}
                </Text>
              </View>
            ))}
          </View>
        ) : null}

        {doc.rules?.length ? (
          /* "How this pass works" (prototype fgm-plan-truths): rule title
             with a small explanation; replaces the plain benefits list. */
          <View
            className="mt-3"
            style={{ gap: 10 }}
            accessibilityLabel="How this pass works"
          >
            {doc.rules.map((rule) => (
              <View key={rule._key} className="flex-row" style={{ gap: 8 }}>
                <Text category="s1" style={{ color: primaryColor }}>
                  ✓
                </Text>
                <View className="flex-1">
                  <Text category="s2">{rule.title}</Text>
                  {rule.detail ? (
                    <Text category="c1" appearance="hint">
                      {rule.detail}
                    </Text>
                  ) : null}
                </View>
              </View>
            ))}
          </View>
        ) : benefits.length ? (
          <View className="mt-3">
            {benefits.map((benefit) => (
              <Text key={benefit} category="p2">
                • {benefit}
              </Text>
            ))}
          </View>
        ) : null}

        {doc.kind === "program" && (doc.comparison?.length ?? 0) > 0 ? (
          <Pressable
            testID="v3-product-comparison-link"
            onPress={() => onOpenComparison(product)}
            className="mt-2"
          >
            <Text category="s2" style={{ color: primaryColor }}>
              Compare options
            </Text>
          </Pressable>
        ) : null}

        <Spacer margin={6} />
        <Button
          testID={`v3-purchase-${doc.slug.current}`}
          onPress={() => onPurchase(selected, quantity)}
        >
          Continue
        </Button>
        {doc.giftingEnabled ? (
          <>
            <Spacer margin={4} />
            <Button
              testID={`v3-gift-${doc.slug.current}`}
              appearance="outline"
              onPress={() => onGift(selected.planPackage.id)}
            >
              {doc.giftButtonText || "Purchase as a gift"}
            </Button>
          </>
        ) : null}
      </View>
    </View>
  );
};

export default ProductCardV3;
